/**
 * Todo Store (Zustand)
 *
 * Manages todos, projects, and sections from the API.
 * Provides NLP input parsing for quick-add preview.
 *
 * No console.log, no any types.
 */

import { create } from 'zustand';
import type { Todo, TodoProject, TodoSection } from '@signa/shared';
import { parseTaskInput } from '../lib/nlpDateParser';
import type { Priority, ParsedTaskInput } from '../components/todo/todoTypes';
import {
  todoApi,
  apiTodoToTodo,
  apiProjectToProject,
  apiSectionToSection,
} from '../lib/todo-api';
import { DEMO_TODOS, DEMO_PROJECTS, DEMO_SECTIONS } from './demoData';

// Types

export interface ExplicitTodoData {
  title: string;
  projectId: string;
  sectionId?: string;
  descriptionText?: string;
  priority?: 1 | 2 | 3 | 4;
  dueDate?: string;
  labels?: string[];
}

interface TodoState {
  // Data
  todos: Todo[];
  projects: TodoProject[];
  sections: TodoSection[];

  // Kanban view
  activeProjectId: string | null;
  activeSectionId: string | null;
  filterCompleted: boolean;

  // UI
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // NLP input
  nlpInput: string;
  parsedPreview: ParsedTaskInput | null;

  // Actions
  loadTodos(): Promise<void>;
  createTodo(input: string): Promise<void>; // NLP mode
  createTodoExplicit(data: ExplicitTodoData): Promise<void>;
  updateTodo(id: string, data: Partial<Todo>): Promise<void>;
  deleteTodo(id: string): Promise<void>;
  toggleComplete(id: string): Promise<void>;
  reorder(projectId: string, orderedIds: string[]): Promise<void>;
  bulkComplete(ids: string[], completed: boolean): Promise<void>;
  createProject(name: string, color?: string, icon?: string): Promise<void>;
  updateProject(id: string, data: Partial<TodoProject>): Promise<void>;
  deleteProject(id: string): Promise<void>;
  createSection(name: string, projectId: string): Promise<void>;
  updateSection(id: string, data: Partial<TodoSection>): Promise<void>;
  deleteSection(id: string): Promise<void>;
  setNlpInput(input: string): void;
  clearError(): void;
  setActiveProject(id: string | null): void;
  setActiveSection(id: string | null): void;
  setFilterCompleted(v: boolean): void;
  loadDemo(): void;
}

// Store

export const useTodoStore = create<TodoState>((set, get) => ({
  // Initial state
  todos: [],
  projects: [],
  sections: [],
  activeProjectId: null,
  activeSectionId: null,
  filterCompleted: false,
  isLoading: false,
  isSaving: false,
  error: null,
  nlpInput: '',
  parsedPreview: null,

  // loadTodos

  loadTodos: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await todoApi.list();
      set({
        todos: res.todos.map(apiTodoToTodo),
        projects: res.projects.map(apiProjectToProject),
        sections: res.sections.map(apiSectionToSection),
        isLoading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load todos';
      set({ isLoading: false, error: message });
    }
  },

  loadDemo: () => {
    set({
      todos: DEMO_TODOS,
      projects: DEMO_PROJECTS,
      sections: DEMO_SECTIONS,
      activeProjectId: null,
      activeSectionId: null,
      filterCompleted: false,
      isLoading: false,
      isSaving: false,
      error: null,
    });
  },

  // createTodo (NLP mode)

  createTodo: async (input: string) => {
    const { nlpInput } = get();
    if (!nlpInput.trim()) return;

    const optimistic: Todo = {
      id: `optimistic-${Date.now()}`,
      userId: '',
      title: parseTaskInput(nlpInput).title || nlpInput,
      description: '',
      priority: parseTaskInput(nlpInput).priority ?? 3,
      dueDate: parseTaskInput(nlpInput).date?.toISOString(),
      isRecurring: false,
      labels: [],
      position: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Optimistic insert
    set((s) => ({
      todos: [optimistic, ...s.todos],
      nlpInput: '',
      parsedPreview: null,
      isSaving: true,
    }));

    try {
      const res = await todoApi.createNLP(nlpInput);
      const created = apiTodoToTodo(res.todo);
      set((s) => ({
        todos: s.todos.map((t) => (t.id === optimistic.id ? created : t)),
        isSaving: false,
      }));
    } catch (err) {
      // Revert optimistic insert
      set((s) => ({
        todos: s.todos.filter((t) => t.id !== optimistic.id),
        error: err instanceof Error ? err.message : 'Failed to create todo',
        isSaving: false,
      }));
    }
  },

  // createTodoExplicit

  createTodoExplicit: async (data: ExplicitTodoData) => {
    const optimistic: Todo = {
      id: `optimistic-${Date.now()}`,
      userId: '',
      title: data.title,
      description: data.descriptionText,
      priority: data.priority ?? 3,
      dueDate: data.dueDate,
      isRecurring: false,
      labels: data.labels ?? [],
      projectId: data.projectId,
      sectionId: data.sectionId,
      position: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    set((s) => ({ todos: [optimistic, ...s.todos], isSaving: true }));

    try {
      const res = await todoApi.createExplicit(data);
      const created = apiTodoToTodo(res.todo);
      set((s) => ({
        todos: s.todos.map((t) => (t.id === optimistic.id ? created : t)),
        isSaving: false,
      }));
    } catch (err) {
      set((s) => ({
        todos: s.todos.filter((t) => t.id !== optimistic.id),
        error: err instanceof Error ? err.message : 'Failed to create todo',
        isSaving: false,
      }));
    }
  },

  // updateTodo

  updateTodo: async (id: string, data: Partial<Todo>) => {
    const previous = get().todos.find((t) => t.id === id);
    if (!previous) return;

    // Optimistic update
    set((s) => ({
      todos: s.todos.map((t) => (t.id === id ? { ...t, ...data } : t)),
      isSaving: true,
    }));

    try {
      const res = await todoApi.update(id, data);
      const updated = apiTodoToTodo(res.todo);
      set((s) => ({
        todos: s.todos.map((t) => (t.id === id ? updated : t)),
        isSaving: false,
      }));
    } catch (err) {
      // Revert
      set((s) => ({
        todos: s.todos.map((t) => (t.id === id ? previous : t)),
        error: err instanceof Error ? err.message : 'Failed to update todo',
        isSaving: false,
      }));
    }
  },

  // deleteTodo

  deleteTodo: async (id: string) => {
    const previous = get().todos;

    set((s) => ({ todos: s.todos.filter((t) => t.id !== id), isSaving: true }));

    try {
      await todoApi.delete(id);
      set({ isSaving: false });
    } catch (err) {
      set({
        todos: previous,
        error: err instanceof Error ? err.message : 'Failed to delete todo',
        isSaving: false,
      });
    }
  },

  // toggleComplete

  toggleComplete: async (id: string) => {
    const todo = get().todos.find((t) => t.id === id);
    if (!todo) return;

    const wasCompleted = todo.completedAt != null;
    const now = new Date().toISOString();

    // Optimistic toggle
    set((s) => ({
      todos: s.todos.map((t) =>
        t.id === id
          ? {
              ...t,
              completedAt: wasCompleted ? undefined : now,
              updatedAt: now,
            }
          : t,
      ),
      isSaving: true,
    }));

    try {
      const res = await todoApi.toggleComplete(id);
      const updated = apiTodoToTodo(res.todo);
      set((s) => ({
        todos: s.todos.map((t) => (t.id === id ? updated : t)),
        isSaving: false,
      }));
    } catch (err) {
      // Revert
      set((s) => ({
        todos: s.todos.map((t) => (t.id === id ? todo : t)),
        error: err instanceof Error ? err.message : 'Failed to toggle complete',
        isSaving: false,
      }));
    }
  },

  // reorder

  reorder: async (projectId: string, orderedIds: string[]) => {
    const previous = get().todos;

    // Optimistic: update positions based on new order
    set((s) => ({
      todos: s.todos.map((t) => {
        if (t.projectId !== projectId) return t;
        const idx = orderedIds.indexOf(t.id);
        return idx !== -1 ? { ...t, position: idx } : t;
      }),
      isSaving: true,
    }));

    try {
      await todoApi.reorder(projectId, orderedIds);
      set({ isSaving: false });
    } catch (err) {
      set({
        todos: previous,
        error: err instanceof Error ? err.message : 'Failed to reorder todos',
        isSaving: false,
      });
    }
  },

  // bulkComplete

  bulkComplete: async (ids: string[], completed: boolean) => {
    const previous = get().todos;
    const now = new Date().toISOString();

    set((s) => ({
      todos: s.todos.map((t) =>
        ids.includes(t.id)
          ? { ...t, completedAt: completed ? now : undefined, updatedAt: now }
          : t,
      ),
      isSaving: true,
    }));

    try {
      await todoApi.bulkComplete(ids, completed);
      set({ isSaving: false });
    } catch (err) {
      set({
        todos: previous,
        error: err instanceof Error ? err.message : 'Failed to bulk complete',
        isSaving: false,
      });
    }
  },

  // Projects

  createProject: async (name: string, color?: string, icon?: string) => {
    set({ isSaving: true });
    try {
      const res = await todoApi.createProject({ name, color, icon });
      const created = apiProjectToProject(res.project);
      set((s) => ({ projects: [...s.projects, created], isSaving: false }));
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to create project',
        isSaving: false,
      });
    }
  },

  updateProject: async (id: string, data: Partial<TodoProject>) => {
    const previous = get().projects.find((p) => p.id === id);
    set((s) => ({
      projects: s.projects.map((p) => (p.id === id ? { ...p, ...data } : p)),
      isSaving: true,
    }));
    try {
      const res = await todoApi.updateProject(id, data);
      const updated = apiProjectToProject(res.project);
      set((s) => ({
        projects: s.projects.map((p) => (p.id === id ? updated : p)),
        isSaving: false,
      }));
    } catch (err) {
      set((s) => ({
        projects: s.projects.map((p) => (p.id === id ? (previous ?? p) : p)),
        error: err instanceof Error ? err.message : 'Failed to update project',
        isSaving: false,
      }));
    }
  },

  deleteProject: async (id: string) => {
    const previous = get().projects;
    set((s) => ({ projects: s.projects.filter((p) => p.id !== id), isSaving: true }));
    try {
      await todoApi.deleteProject(id);
      set({ isSaving: false });
    } catch (err) {
      set({
        projects: previous,
        error: err instanceof Error ? err.message : 'Failed to delete project',
        isSaving: false,
      });
    }
  },

  // Sections

  createSection: async (name: string, projectId: string) => {
    set({ isSaving: true });
    try {
      const res = await todoApi.createSection({ name, projectId });
      const created = apiSectionToSection(res.section);
      set((s) => ({ sections: [...s.sections, created], isSaving: false }));
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to create section',
        isSaving: false,
      });
    }
  },

  updateSection: async (id: string, data: Partial<TodoSection>) => {
    const previous = get().sections.find((s) => s.id === id);
    set((s) => ({
      sections: s.sections.map((sec) => (sec.id === id ? { ...sec, ...data } : sec)),
      isSaving: true,
    }));
    try {
      const res = await todoApi.updateSection(id, data);
      const updated = apiSectionToSection(res.section);
      set((s) => ({
        sections: s.sections.map((sec) => (sec.id === id ? updated : sec)),
        isSaving: false,
      }));
    } catch (err) {
      set((s) => ({
        sections: s.sections.map((sec) => (sec.id === id ? (previous ?? sec) : sec)),
        error: err instanceof Error ? err.message : 'Failed to update section',
        isSaving: false,
      }));
    }
  },

  deleteSection: async (id: string) => {
    const previous = get().sections;
    set((s) => ({ sections: s.sections.filter((sec) => sec.id !== id), isSaving: true }));
    try {
      await todoApi.deleteSection(id);
      set({ isSaving: false });
    } catch (err) {
      set({
        sections: previous,
        error: err instanceof Error ? err.message : 'Failed to delete section',
        isSaving: false,
      });
    }
  },

  // NLP input

  setNlpInput: (input: string) => {
    const parsed: ParsedTaskInput | null = input.trim()
      ? parseTaskInput(input)
      : null;
    set({ nlpInput: input, parsedPreview: parsed });
  },

  // Misc

  clearError: () => set({ error: null }),
  setActiveProject: (id: string | null) => set({ activeProjectId: id }),
  setActiveSection: (id: string | null) => set({ activeSectionId: id }),
  setFilterCompleted: (v: boolean) => set({ filterCompleted: v }),
}));

// Demo Mode
if (typeof window !== 'undefined') {
  window.addEventListener('signa:demo-mode', () => {
    // Only load demo if not already in demo mode
    const state = useTodoStore.getState();
    if (state.projects.length === 0 || state.todos.length === 0) {
      useTodoStore.getState().loadDemo();
    }
  });
}
